/**
 * "Things that should wake someone up."
 *
 * The product doc names three reasons this panel exists — things to **do**,
 * things to **watch**, and things that should **wake someone up** — and the
 * third had no surface at all. A crawl that failed on six brands last night
 * was discoverable only by opening the Crawl tab and reading a job list;
 * a brand application sitting in `pending_review` was not discoverable from
 * the panel at all, since nothing rendered it.
 *
 * This provider polls one cheap endpoint and shares the result with both the
 * nav (count badges, so a section says it needs attention from anywhere) and
 * Home (the full list). One request, two consumers — a badge that costs a
 * second round-trip tends to get dropped the first time someone optimises.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import adminApi from './api';
import { useAdminAuth } from './auth';

const Ctx = createContext({ items: [], badges: {}, health: null, loading: true, refresh: () => {} });

const POLL_MS = 60_000;

export function AttentionProvider({ children }) {
  const { admin } = useAdminAuth();
  const [state, setState] = useState({ items: [], badges: {}, health: null, loading: true });

  const refresh = useCallback(async () => {
    // No session, nothing to poll. This provider wraps the router, so it also
    // mounts on /login — firing an unauthenticated request there would 401 on
    // every visit to the sign-in page for no benefit.
    if (!admin) {
      setState({ items: [], badges: {}, health: null, loading: false });
      return;
    }
    try {
      const { data } = await adminApi.get('/attention');
      setState({
        items: data.items || [],
        badges: data.badges || {},
        health: data.health || null,
        loading: false,
      });
    } catch {
      // A failed attention poll must never blank the panel or raise a toast on
      // a timer — it degrades to "no badges", and Home says so in place of the
      // list rather than implying all-clear.
      setState((s) => ({ ...s, loading: false }));
    }
  }, [admin]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAttention = () => useContext(Ctx);
